import React, { useId } from 'react';
import { CUSTOMER_TYPE, type CustomerType } from '@origin-digital/sales-common';
import styled from 'styled-components';
import {
  Box,
  Button,
  Heading,
  Sheet,
  Stack,
  Text,
  useMediaQuery,
  useTheme,
} from '@origin-digital/ods-core';

const TITLE = 'You can still get connected. Just one extra step';

const DialogHeading = styled(Heading)`
  &&& {
    line-height: 1.166667;
  }
`;

const CESDialogTitle = ({ children }: { children: React.ReactNode }) => (
  <Stack space="large">
    <img
      src="https://www.originenergy.com.au/cdn/images/originenergy/image/upload/pictograms/pictogram_apartment.svg"
      alt=""
      width={96}
      height={96}
    />
    <DialogHeading variant={{ xs: 'title2', md: 'title3' }} component="h2">
      {children}
    </DialogHeading>
  </Stack>
);

export const CESDialog = ({
  open,
  onClose,
  href,
  customerType,
}: {
  open: boolean;
  onClose: () => void;
  href: string;
  customerType: CustomerType;
}) => {
  const id = `address-ces-dialog-${useId()}`;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Sheet
      id={id}
      title={TITLE}
      titleComponent={CESDialogTitle}
      open={open}
      onClose={onClose}
      headerMargin="large"
      padding={{ xs: 'large', md: 'xlarge' }}
      paddingBottom="xlarge"
      borderRadius={isMobile ? 'medium' : 'small'}
      dialogMaxWidth="sm"
      muiProps={{
        PaperProps: {
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': `${id}-title`,
          'aria-describedby': `${id}-content`,
        },
      }}
    >
      <Stack space="large">
        <Text>
          Your building is part of an embedded network, which means energy here is arranged slightly
          differently to a standalone{' '}
          {customerType === CUSTOMER_TYPE.BUSINESS ? 'business' : 'home'}. Select your address in
          the next step and we'll set you up with the right service for your building.
        </Text>
        <Box>
          <Button href={href} fullWidth={{ xs: true, md: false }}>
            Show available energy services
          </Button>
        </Box>
      </Stack>
    </Sheet>
  );
};
